import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { GridColDef } from '@mui/x-data-grid-pro';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { gridBooleanCell, dataGridCol } from '@shared/components/grid/common';
import { newGridRecordId } from '@shared/commonFunctions';
import { GridColTextInput } from '@shared/components/grid/GridColTextInput';
import { GridColSelect } from '@shared/components/grid/GridColSelect';
import { SERVICES } from '@shared/constants/apiConfig';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { IOption, IStipType } from '@shared/interfaces';
import { ViewHeightWrapper } from '@styles/CommonStyles';
import { requestCreateStipType, requestDeleteStipType, requestUpdateStipType } from './queries';
import { stipTypeEditorValidationSchema } from './validation';

const UPLOAD_TYPE_OPTIONS: IOption[] = [
  {
    value: 'Contact',
    label: 'Contact',
  },
  {
    value: 'Asset',
    label: 'Asset',
  },
  {
    value: 'Deal',
    label: 'Deal',
  },
];

export const StipTypeEditor = () => {
  const { data } = useSWR(`/${SERVICES.DOCUMENT}/stipTypes`);
  const [stipTypes, setStipTypes] = useState(data);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setStipTypes(data);
    }
  }, [data]);

  const columns: GridColDef[] = [
    dataGridCol('name', {
      width: 180,
      renderEditCell: (params) => <GridColTextInput params={params} />,
    }),
    dataGridCol('relatedToEntity', {
      headerName: 'Upload to Type',
      width: 140,
      renderEditCell: (params) => (
        <GridColSelect
          hideNoneOption
          options={UPLOAD_TYPE_OPTIONS}
          params={params}
        />
      ),
    }),
    dataGridCol('isRequired', {
      width: 140,
      headerName: 'Required',
      ...gridBooleanCell,
    }),
    dataGridCol('isRequiredFromCoBorrower', {
      headerName: 'Required From Co',
      ...gridBooleanCell,
    }),
    dataGridCol('willComeFromCustomer', {
      width: 140,
      headerName: 'Comes From Customer',
      ...gridBooleanCell,
    }),
    dataGridCol('isReviewable', {
      headerName: 'Reviewable',
      ...gridBooleanCell,
    }),
    dataGridCol('defaultNote', {
      minWidth: 180,
      flex: 1,
    }),
    dataGridCol('cp_Enabled', {
      headerName: 'CP Enabled',
      ...gridBooleanCell,
    }),
    dataGridCol('isHidden', {
      ...gridBooleanCell,
    }),
  ].map((i) => ({ ...i, editable: true }));

  const gridActions = {
    onSave: async (row: IStipType) => {
      setLoading(true);
      row.id === newGridRecordId ? await requestCreateStipType(row) : await requestUpdateStipType(row);
      setLoading(false);
    },
    onDelete: async (row: IStipType) => {
      setLoading(true);
      await requestDeleteStipType(row.id);
      setLoading(false);
    },
  };

  return (
    <ViewHeightWrapper>
      <LoaderBackdrop open={loading} />
      <PhoenixGrid
        withHeader
        showClearAllFilter
        name="Stip Type"
        pageSize={30}
        columns={columns}
        loading={!stipTypes}
        rows={stipTypes}
        actions={gridActions}
        validateSchema={stipTypeEditorValidationSchema}
      />
    </ViewHeightWrapper>
  );
};
